import { Dispatch, SetStateAction } from "react";
import moment from "moment-timezone";
import DropdownItem from "react-bootstrap/esm/DropdownItem";
import Form from "react-bootstrap/esm/Form";
// components
import DropdownToggle from "components/dropdown-toggle/DropdownToggle";
import DatePicker from "components/date-picker/DatePicker";
import PlanFieldLabel from "../plan-field-label";
// constants
import { REMINDER_DAYS, REMINDER_TYPES } from "../constants";
import messages from "messages";
// utils
import getClasses from "utils/getClasses";
// styles
import styles from "./ReminderFields.module.scss";
// hooks
import { useUser } from "contexts/UserContext";

type Props<T> = {
  className?: string;
  readOnly: boolean;
  value: T;
  setValue?: Dispatch<SetStateAction<T>>;
};

const ReminderFields = <T extends TreatmentPlan>({
  readOnly,
  value,
  setValue,
  className,
}: Props<T>) => {
  const { defaultUserTimezone } = useUser();
  return (
    <div
      className={getClasses(
        styles["reminder-fields"],
        readOnly && styles["reminder-fields--readOnly"],
        className,
      )}
    >
      <Form.Group
        className={styles["reminder-fields__reminder-option"]}
        controlId="frequency"
      >
        <PlanFieldLabel
          label={messages.treatmentFrequency}
          readOnly={readOnly}
        />
        {readOnly ? (
          <div className={styles["reminder-fields__reminder-control"]}>
            {value.frequency}
          </div>
        ) : (
          <Form.Control
            className={styles["reminder-fields__reminder-control"]}
            type="text"
            placeholder={messages.every2Weeks}
            defaultValue={value.frequency || ""}
            onChange={(e) =>
              setValue &&
              setValue((s) => ({
                ...s,
                frequency: e.target.value,
              }))
            }
            readOnly={readOnly}
            plaintext={readOnly}
          />
        )}
      </Form.Group>
      <Form.Group
        className={styles["reminder-fields__reminder-option"]}
        controlId="reminder"
      >
        <PlanFieldLabel
          label={messages.setReminderToReviewPlan}
          readOnly={readOnly}
        />

        <div className={styles["reminder-fields__reminder-option-group"]}>
          <div
            className={getClasses(
              styles["reminder-fields__reminder-option"],
              styles["reminder-fields__reminder-option--sub"],
            )}
          >
            {readOnly ? (
              <div>
                {REMINDER_TYPES[value.reminder?.type || -1] ||
                  REMINDER_TYPES.NONE}
              </div>
            ) : (
              <DropdownToggle
                title={
                  REMINDER_TYPES[value.reminder?.type || -1] ||
                  REMINDER_TYPES.NONE
                }
                className={getClasses(
                  styles["reminder-fields__reminder-control"],
                )}
              >
                {Object.entries(REMINDER_TYPES).map(([k, v]) => (
                  <DropdownItem
                    key={k}
                    disabled={value.reminder?.type === k}
                    onClick={() =>
                      setValue &&
                      setValue((s) => ({
                        ...s,
                        reminder: {
                          type: k,

                          ...(k !== "NONE" && {
                            value: k === "AFTER" ? 9 : Date.now(),
                          }),
                        },
                      }))
                    }
                  >
                    {v}
                  </DropdownItem>
                ))}
              </DropdownToggle>
            )}
          </div>

          <div
            className={getClasses(
              styles["reminder-fields__reminder-option"],
              styles["reminder-fields__reminder-option--sub"],
            )}
          >
            {value.reminder?.type === "AFTER" &&
              (readOnly ? (
                <div>{value.reminder?.value + " " + messages.days}</div>
              ) : (
                <DropdownToggle
                  title={value.reminder?.value + " " + messages.days}
                  className={styles["reminder-fields__reminder-control"]}
                >
                  {REMINDER_DAYS.map((k) => (
                    <DropdownItem
                      key={k}
                      onClick={() =>
                        setValue &&
                        setValue((s) => ({
                          ...s,
                          reminder: { type: "AFTER", value: k },
                        }))
                      }
                    >
                      {k + " " + messages.days}
                    </DropdownItem>
                  ))}
                </DropdownToggle>
              ))}

            {value.reminder?.type === "ONDATE" &&
              (readOnly ? (
                <div>
                  {moment
                    .tz(value.reminder?.value, defaultUserTimezone)
                    .format("MM/D/yyyy")}
                </div>
              ) : (
                <DatePicker
                  wrapperClassName={styles["reminder-fields__reminder-control"]}
                  returnStartOfDay
                  timezone={defaultUserTimezone}
                  dateFormat={"MM/dd/yyyy"}
                  selected={new Date(value.reminder?.value || 0)}
                  onChange={(date: Date) =>
                    setValue &&
                    setValue((s) => ({
                      ...s,
                      reminder: {
                        type: "ONDATE",
                        value: date.toISOString(),
                      },
                    }))
                  }
                  placeholderText={""}
                />
              ))}
          </div>
        </div>
      </Form.Group>
    </div>
  );
};

export default ReminderFields;
